// Бинарный wire-протокол. ДОЛЖЕН совпадать с server/internal/proto/proto.go
// и описанием в shared/PROTOCOL.md. Все целые little-endian.

export const MsgType = {
  Hello:       1, // S->C: ваш playerId, начальный snapshot
  Input:       2, // C->S
  Snapshot:    3, // S->C
  EventBatch:  4, // S->C: события (BlockBroken, Shot, Death, ...)
  Chat:        5, // двусторонний (reliable channel)
  Pong:        6, // S->C ответ на ping
  ChunkInit:   7, // S->C: инициализация воксельного объекта
  PickupInit:  8, // S->C (v8): список pickup'ов сцены (координаты/тип/state)
} as const;

export const EvType = {
  BlockBroken:         1,
  Shot:                2,
  Death:               3,
  Respawn:             4,
  ChunkBroken:         5,
  FallingGroupSpawn:   6,
  FallingGroupUpdate:  7,
  FallingGroupShatter: 8,
  ChunkPoseUpdate:     9,
  NpcShot:            10,
  RoomClosed:         11,
  MatchEnd:           12,
  // v8 additions
  Pickup:             13,
  PortalEnter:        14,
  /** v9: сбит кубик тела NPC (v11+ не для тела) */
  NpcBlockBroken:     15,
  /** v11: попадание в тело игрока (мир. точка + осколок) */
  BodyDamaged:        16,
  NpcBodyDamaged:     17,
  /** v20: фаза дверей особняка (анимация + коллизия на клиенте) */
  MansionDoor:        18,
  InteriorDoor:       19,
  /** v22: старт раунда */
  RoundStart:         20,
  /** v22: конец раунда (победитель + счёт) */
  RoundEnd:           21,
  // weapons: события оружия (зеркало proto.EvWeaponFire…EvPickupRemove)
  /** выстрел/взмах/бросок любого оружия (звук, вспышка, пламя у чужих) */
  WeaponFire:         30,
  /** взрыв ракеты/гранаты */
  Explosion:          31,
  /** новый подбираемый предмет (трофей с убитого) */
  PickupSpawn:        32,
  /** предмет исчез (взят или истёк срок) */
  PickupRemove:       33,
  /** story: событие сюжетного режима (код StoryEv) */
  Story:              40,
} as const;

// ─── story: сюжетный режим «Книга заклинаний» (зеркало server/internal/proto/story.go) ───
/** Вид сюжетного существа. */
export const StoryKind = {
  Zombie:     1,
  GhostGirl:  2,
  GhostWoman: 3,
  Hag:        4,
  Painting:   5,
  Book:       6,
} as const;
/** Состояние существа (клиент выбирает клип и эффект). */
export const StoryState = {
  Idle:     0,
  Move:     1,
  Attack:   2,
  Dying:    3,
  Dead:     4,
  Dissolve: 5,
  Hidden:   6,
  Awake:    7,
} as const;
/** Этап сюжета (0 — режим не сюжетный). */
export const StoryStage = {
  None:    0,
  Enter:   1,
  Find:    2,
  Boss:    3,
  Take:    4,
  Victory: 5,
} as const;
export const StoryFlag = {
  Waypoint: 1 << 0,
  BossLive: 1 << 1,
} as const;
/** Код события EvType.Story. */
export const StoryEv = {
  Stage:    1,
  Whisper:  2,
  MobDie:   3,
  Dissolve: 4,
  Scare:    5,
  Hit:      6,
  Awake:    7,
  Book:     8,
  Victory:  9,
  Restart:  10,
  MobHurt:  11,
  Respawn:  12,
} as const;

/** story: одно существо в снапшоте. */
export interface SnapshotStoryMob {
  id: number;    // u16
  kind: number;  // u8 StoryKind
  state: number; // u8 StoryState
  x: number; y: number; z: number; // f32; у картины z — центр полотна
  yaw: number;   // f32; у картины — куда смотрит полотно
  hp: number;    // u8, % от максимума
  age: number;   // u16, тиков в текущем состоянии
  variant: number; // u8, номер картины
}

/** story: сюжетный блок в конце снапшота. stage=0 — режим не сюжетный. */
export interface SnapshotStory {
  stage: number;
  flags: number;
  timeSec: number;
  kills: number;
  ghosts: number;
  wx: number; wy: number; wz: number;
  mobs: SnapshotStoryMob[];
}

const EMPTY_STORY: SnapshotStory = {
  stage: 0, flags: 0, timeSec: 0, kills: 0, ghosts: 0, wx: 0, wy: 0, wz: 0, mobs: [],
};

function readStory(r: Reader): SnapshotStory {
  if (r.remaining() < 1) return EMPTY_STORY; // старый сервер — блока нет
  const stage = r.u8();
  if (stage === 0) return EMPTY_STORY;
  const flags = r.u8();
  const timeSec = r.u16();
  const kills = r.u16();
  const ghosts = r.u16();
  const wx = r.f32(), wy = r.f32(), wz = r.f32();
  const n = r.u8();
  const mobs: SnapshotStoryMob[] = [];
  for (let i = 0; i < n; i++) {
    mobs.push({
      id: r.u16(), kind: r.u8(), state: r.u8(),
      x: r.f32(), y: r.f32(), z: r.f32(), yaw: r.f32(),
      hp: r.u8(), age: r.u16(), variant: r.u8(),
    });
  }
  return { stage, flags, timeSec, kills, ghosts, wx, wy, wz, mobs };
}

function writeStory(w: Writer, s: SnapshotStory | undefined): void {
  if (!s || s.stage === 0) {
    w.u8(0);
    return;
  }
  w.u8(s.stage);
  w.u8(s.flags);
  w.u16(s.timeSec);
  w.u16(s.kills);
  w.u16(s.ghosts);
  w.f32(s.wx); w.f32(s.wy); w.f32(s.wz);
  const n = Math.min(255, s.mobs.length);
  w.u8(n);
  for (const m of s.mobs.slice(0, n)) {
    w.u16(m.id); w.u8(m.kind); w.u8(m.state);
    w.f32(m.x); w.f32(m.y); w.f32(m.z); w.f32(m.yaw);
    w.u8(m.hp); w.u16(m.age); w.u8(m.variant);
  }
}

/** weapons: число видов оружия (зеркало proto.WeaponCount). */
export const WEAPON_COUNT = 9;
/** weapons: биты SnapshotPlayer/SnapshotNpc.weaponFlags. */
export const WeaponFlag = {
  Burning: 1 << 0,
  Firing:  1 << 1,
} as const;
/** weapons: SnapshotProjectile.kind. */
export const ProjectileKind = {
  Rocket:  1,
  Grenade: 2,
} as const;
/** weapons: EvWeaponFire.shooterKind. */
export const ShooterKind = {
  Player: 0,
  Npc:    1,
} as const;

/** Совпадает с server/internal/proto ShotSurf*. */
export const ShotSurf = {
  Miss: 0,
  Geometry: 1,
  Player: 2,
  Npc: 3,
} as const;
export type EvTypeValue = typeof EvType[keyof typeof EvType];

/** Зеркало proto.NpcBodyDamagedFlagHeadshot (EvNpcBodyDamaged.flags). */
export const NpcBodyDamagedFlagHeadshot = 1;

/** PickupType — зеркало server proto.PickupType*. */
export const PickupType = {
  Medkit:       0,
  Ammo:         1,
  WeaponRifle:  2,
  /** weapons: оружие вида id — WeaponBase+id (16..24); винтовка из уровня — WeaponRifle. */
  WeaponBase:   16,
  /** artifacts: реликвии (amount — серебро). */
  RelicMedallion: 5,
  RelicSkull:     6,
  RelicCrystal:   7,
} as const;

/** artifacts: PickupInit.artifact — зеркало proto.PickupArtifact*. */
export const PickupArtifact = {
  None: 0,
  /** Выпал из разрушенного предмета: светится, from* — точка вылета. */
  Drop: 1,
} as const;
export type PickupTypeValue = number;

// v8: ChunkInit.Role (классификация чанков — wall/floor/ceiling/terrain/
//     stair/door/window/prop/decor). Должно совпадать с server/proto.go.
export const PROTOCOL_VERSION = 26;

/** v22: режим матча (Hello.matchMode). Зеркало proto.MatchMode*. */
export const MatchMode = {
  FFA: 0,
  TDM: 1,
} as const;

/** v22: фаза матча (Snapshot.matchPhase). Зеркало proto.MatchPhase*. */
export const MatchPhase = {
  Idle:      0, // SP: матча нет
  Warmup:    1,
  Countdown: 2,
  Playing:   3,
  RoundEnd:  4,
  MatchEnd:  5,
} as const;

/** v22: команды. Зеркало proto.Team*. */
export const Team = {
  A:    0,
  B:    1,
  None: 255,
} as const;

/** v12: размер битмаски вокселей тела в Snapshot (10×10×24 бит). */
export const BODY_VOX_GRID_BYTES = 300;

// ChunkRole — зеркало server-side game.ChunkRole. Клиент использует для
// classification (collision path, shader path, transparency для window).
export const ChunkRole = {
  Wall:    0,
  Floor:   1,
  Ceiling: 2,
  Terrain: 3,
  Stair:   4,
  Door:    5,
  Window:  6,
  Prop:    7,
  Decor:   8,
} as const;
export type ChunkRoleValue = typeof ChunkRole[keyof typeof ChunkRole];

/** Indestructible sentinel. Strength=255 означает что пули не ломают куб;
 *  клиент не спавнит debris, хитскан по чанку всё равно блокирует (кубы живы). */
export const STRENGTH_INDESTRUCTIBLE = 255;

export const RoomKind = {
  Single:    0,
  DMPublic:  1,
  DMPrivate: 2,
} as const;
export type RoomKindValue = typeof RoomKind[keyof typeof RoomKind];

export const RoomClosedReason = {
  OwnerLeft: 1,
  Empty:     2,
  Server:    3,
  /** v22: матч доигран (все раунды сыграны) */
  MatchEnd:  4,
} as const;

/** v20: тип точки взаимодействия в HelloInteractHotspot.kind */
export const HelloInteractKindMansionDoor = 1;

export interface HelloInteractHotspot {
  kind: number;
  x: number;
  y: number;
  z: number;
  radius: number;
  chunkA: number;
  chunkB: number;
}

// --- write helpers ---
export class Writer {
  private buf: ArrayBuffer;
  private dv: DataView;
  private off = 0;
  constructor(capacity = 1024) {
    this.buf = new ArrayBuffer(capacity);
    this.dv = new DataView(this.buf);
  }
  reset(): void { this.off = 0; }
  private _byteView: Uint8Array | null = null;
  /** Срез по текущему `off`; view кэшируется при росте `buf` в `grow()`. */
  bytes(): Uint8Array {
    if (!this._byteView || this._byteView.buffer !== this.buf) {
      this._byteView = new Uint8Array(this.buf);
    }
    return this._byteView.subarray(0, this.off);
  }
  private grow(extra: number): void {
    if (this.off + extra <= this.buf.byteLength) return;
    let cap = this.buf.byteLength * 2;
    while (cap < this.off + extra) cap *= 2;
    const nb = new ArrayBuffer(cap);
    new Uint8Array(nb).set(new Uint8Array(this.buf, 0, this.off));
    this.buf = nb;
    this.dv = new DataView(this.buf);
    this._byteView = null;
  }
  u8(v: number): void { this.grow(1); this.dv.setUint8(this.off, v); this.off += 1; }
  u16(v: number): void { this.grow(2); this.dv.setUint16(this.off, v, true); this.off += 2; }
  u32(v: number): void { this.grow(4); this.dv.setUint32(this.off, v >>> 0, true); this.off += 4; }
  i16(v: number): void { this.grow(2); this.dv.setInt16(this.off, v, true); this.off += 2; }
  f32(v: number): void { this.grow(4); this.dv.setFloat32(this.off, v, true); this.off += 4; }
  str(s: string): void {
    const b = new TextEncoder().encode(s);
    this.u16(b.byteLength);
    this.grow(b.byteLength);
    new Uint8Array(this.buf, this.off, b.byteLength).set(b);
    this.off += b.byteLength;
  }

  /** Сырые байты подряд (фикс. блобы вроде BodyVox v12+). */
  rawAppend(src: Uint8Array): void {
    this.grow(src.byteLength);
    new Uint8Array(this.buf, this.off, src.byteLength).set(src);
    this.off += src.byteLength;
  }
}

const UTF8 = new TextDecoder();

export class Reader {
  private dv!: DataView;
  private off = 0;
  private bytes!: Uint8Array;
  constructor(buf: ArrayBuffer | Uint8Array) {
    this.setBuffer(buf);
  }

  /** Повторное использование одного Reader на hot path (WS onmessage) — без `new Reader` на кадр. */
  reset(buf: ArrayBuffer | Uint8Array): void {
    this.setBuffer(buf);
    this.off = 0;
  }

  private setBuffer(buf: ArrayBuffer | Uint8Array): void {
    if (buf instanceof Uint8Array) {
      this.bytes = buf;
      this.dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    } else {
      this.bytes = new Uint8Array(buf);
      this.dv = new DataView(buf);
    }
  }

  remaining(): number { return this.dv.byteLength - this.off; }
  u8(): number { const v = this.dv.getUint8(this.off); this.off += 1; return v; }
  u16(): number { const v = this.dv.getUint16(this.off, true); this.off += 2; return v; }
  u32(): number { const v = this.dv.getUint32(this.off, true); this.off += 4; return v; }
  i16(): number { const v = this.dv.getInt16(this.off, true); this.off += 2; return v; }
  f32(): number { const v = this.dv.getFloat32(this.off, true); this.off += 4; return v; }
  str(): string {
    const n = this.u16();
    const sl = this.bytes.subarray(this.off, this.off + n);
    this.off += n;
    return UTF8.decode(sl);
  }

  copyBytes(n: number): Uint8Array {
    if (this.remaining() < n) throw new Error("proto: buffer too short");
    const out = new Uint8Array(n);
    out.set(this.bytes.subarray(this.off, this.off + n));
    this.off += n;
    return out;
  }
}

// --- структуры ---

export interface InputCmd {
  seq: number;       // u32
  dtMs: number;      // u16, миллисекунды
  buttons: number;   // u16
  yaw: number;       // f32
  pitch: number;     // f32
}

export function writeInput(w: Writer, cmd: InputCmd): void {
  w.u8(MsgType.Input);
  w.u32(cmd.seq);
  w.u16(cmd.dtMs);
  w.u16(cmd.buttons);
  w.f32(cmd.yaw);
  w.f32(cmd.pitch);
}

export interface SnapshotBodyHit {
  lx: number; ly: number; lz: number; r: number;
}

export interface SnapshotPlayer {
  id: number;       // u8
  x: number;        // f32 (мировые единицы карты)
  y: number;        // f32
  z: number;        // f32
  yaw: number;      // f32
  /** v11: сферы «дыр» в локали тела */
  hitCount: number; // u8, ≤24
  hits: SnapshotBodyHit[]; // ровно hitCount записей
  hp: number;       // u8
  score: number;    // u16
  /** Число попаданий этого игрока в других (стат) */
  shotHits: number;     // u16 (v7)
  deaths: number;   // u16 (v7)
  /** v14: патроны активного слота (-1 = бесконечно). */
  activeAmmo: number; // i16
  /** v22: команда (Team.A/B; Team.None в FFA/SP). */
  team: number; // u8
  /** v22: выигранные раунды (FFA — личные; TDM — победы команды). */
  roundWins: number; // u8
  /** v12: каркас/мясо; v13: + bodyVoxShell — гладкая оболочка. */
  bodyVox: Uint8Array;
  bodyVoxShell: Uint8Array;
  /** characters: персонаж (модель) и макс. HP с апгрейдом — в конце записи. */
  charId: number; // u8
  maxHp: number;  // u8
  /** weapons: активное оружие (id) и флаги WeaponFlag. */
  weapon: number;      // u8
  weaponFlags: number; // u8
}

export interface SnapshotNpc {
  id: number;       // u16
  x: number;        // f32
  y: number;        // f32
  /** v19+: высота ступней (м); совпадает с игроком. */
  z: number;        // f32
  yaw: number;      // f32
  hp: number;       // u8
  /** 0 = alive, 1 = dying (front, FallingBackDeath), 2 = dying (back, deathFromTheBack). */
  state: number;    // u8
  hitCount: number;
  hits: SnapshotBodyHit[];
  bodyVox: Uint8Array;
  bodyVoxShell: Uint8Array;
  /** weapons: оружие бота (id) и флаги WeaponFlag. */
  weapon: number;      // u8
  weaponFlags: number; // u8
}

/** Лёгкое состояние pickup'а (ID + state). Полные атрибуты клиент получает
 *  один раз в PickupInit после Hello. */
export interface SnapshotPickup {
  id: number;    // u16
  state: number; // u8: 0=active, 1=taken
}

export interface Snapshot {
  tick: number;          // u32
  lastAckedInput: number;// u32
  yourId: number;        // u8
  /** v22: состояние матча (см. MatchPhase). В SP phase=Idle, остальное 0. */
  matchPhase: number;    // u8
  roundIdx: number;      // u8, 0-based
  phaseSecsLeft: number; // u16
  teamAWins: number;     // u8
  teamBWins: number;     // u8
  teamAKills: number;    // u16 — фраги команды в текущем раунде (TDM)
  teamBKills: number;    // u16
  players: SnapshotPlayer[];
  npcs: SnapshotNpc[];
  pickups: SnapshotPickup[];
  /** weapons: инвентарь получателя (маска оружия и патроны по видам) + снаряды. */
  yourOwned: number;    // u16
  yourAmmo: number[];   // WEAPON_COUNT × i16 (-1 = бесконечно)
  projectiles: SnapshotProjectile[];
  /** story: сюжетный блок (stage=0 вне режима). */
  story?: SnapshotStory;
}

/** weapons: летящий снаряд (ракета/граната). */
export interface SnapshotProjectile {
  id: number;   // u16
  kind: number; // u8 ProjectileKind
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
}

export function writeSnapshot(w: Writer, s: Snapshot): void {
  w.u8(MsgType.Snapshot);
  w.u32(s.tick);
  w.u32(s.lastAckedInput);
  w.u8(s.yourId);
  // v22: match header.
  w.u8(s.matchPhase);
  w.u8(s.roundIdx);
  w.u16(s.phaseSecsLeft);
  w.u8(s.teamAWins);
  w.u8(s.teamBWins);
  w.u16(s.teamAKills);
  w.u16(s.teamBKills);
  w.u8(s.players.length);
  for (const p of s.players) {
    w.u8(p.id);
    w.f32(p.x);
    w.f32(p.y);
    w.f32(p.z);
    w.f32(p.yaw);
    const hc = Math.min(24, p.hitCount | 0);
    w.u8(hc);
    for (let i = 0; i < hc; i++) {
      const h = p.hits[i]!;
      w.f32(h.lx); w.f32(h.ly); w.f32(h.lz); w.f32(h.r);
    }
    w.u8(p.hp);
    w.u16(p.score);
    w.u16(p.shotHits);
    w.u16(p.deaths);
    w.i16(p.activeAmmo);
    w.u8(p.team);
    w.u8(p.roundWins);
    if (p.bodyVox.byteLength !== BODY_VOX_GRID_BYTES) {
      throw new Error("writeSnapshot: bad bodyVox length");
    }
    if (p.bodyVoxShell.byteLength !== BODY_VOX_GRID_BYTES) {
      throw new Error("writeSnapshot: bad bodyVoxShell length");
    }
    w.rawAppend(p.bodyVox);
    w.rawAppend(p.bodyVoxShell);
    w.u8(p.charId); // characters
    w.u8(p.maxHp);  // characters
    w.u8(p.weapon);      // weapons
    w.u8(p.weaponFlags); // weapons
  }
  w.u8(s.npcs.length);
  for (const n of s.npcs) {
    w.u16(n.id);
    w.f32(n.x);
    w.f32(n.y);
    w.f32(n.z);
    w.f32(n.yaw);
    w.u8(n.hp);
    w.u8(n.state);
    const hcn = Math.min(24, n.hitCount | 0);
    w.u8(hcn);
    for (let i = 0; i < hcn; i++) {
      const h = n.hits[i]!;
      w.f32(h.lx); w.f32(h.ly); w.f32(h.lz); w.f32(h.r);
    }
    if (n.bodyVox.byteLength !== BODY_VOX_GRID_BYTES) {
      throw new Error("writeSnapshot: bad npc bodyVox length");
    }
    if (n.bodyVoxShell.byteLength !== BODY_VOX_GRID_BYTES) {
      throw new Error("writeSnapshot: bad npc bodyVoxShell length");
    }
    w.rawAppend(n.bodyVox);
    w.rawAppend(n.bodyVoxShell);
    w.u8(n.weapon);      // weapons
    w.u8(n.weaponFlags); // weapons
  }
  w.u8(s.pickups.length);
  for (const pk of s.pickups) {
    w.u16(pk.id);
    w.u8(pk.state);
  }
  // weapons: инвентарь получателя + снаряды.
  w.u16(s.yourOwned);
  for (let i = 0; i < WEAPON_COUNT; i++) w.i16(s.yourAmmo[i] ?? 0);
  w.u8(Math.min(255, s.projectiles.length));
  for (const pr of s.projectiles.slice(0, 255)) {
    w.u16(pr.id); w.u8(pr.kind);
    w.f32(pr.x); w.f32(pr.y); w.f32(pr.z);
    w.f32(pr.vx); w.f32(pr.vy); w.f32(pr.vz);
  }
  writeStory(w, s.story); // story
}

export function readSnapshot(r: Reader): Snapshot {
  const tick = r.u32();
  const lastAckedInput = r.u32();
  const yourId = r.u8();
  // v22: match header.
  const matchPhase = r.u8();
  const roundIdx = r.u8();
  const phaseSecsLeft = r.u16();
  const teamAWins = r.u8();
  const teamBWins = r.u8();
  const teamAKills = r.u16();
  const teamBKills = r.u16();
  const n = r.u8();
  const players: SnapshotPlayer[] = [];
  for (let i = 0; i < n; i++) {
    const id = r.u8();
    const x = r.f32(), y = r.f32(), z = r.f32();
    const yaw = r.f32();
    const hitCount = r.u8();
    if (hitCount > 24) throw new Error("bad hitCount");
    const hits: SnapshotBodyHit[] = [];
    for (let j = 0; j < hitCount; j++) {
      hits.push({ lx: r.f32(), ly: r.f32(), lz: r.f32(), r: r.f32() });
    }
    players.push({
      id, x, y, z, yaw, hitCount, hits,
      hp: r.u8(),
      score: r.u16(),
      shotHits: r.u16(),
      deaths: r.u16(),
      activeAmmo: r.i16(),
      team: r.u8(),
      roundWins: r.u8(),
      bodyVox: r.copyBytes(BODY_VOX_GRID_BYTES),
      bodyVoxShell: r.copyBytes(BODY_VOX_GRID_BYTES),
      charId: r.u8(), // characters
      maxHp: r.u8(),  // characters
      weapon: r.u8(),      // weapons
      weaponFlags: r.u8(), // weapons
    });
  }
  const nn = r.u8();
  const npcs: SnapshotNpc[] = [];
  for (let i = 0; i < nn; i++) {
    const id = r.u16();
    const x = r.f32(), y = r.f32(), z = r.f32();
    const yaw = r.f32();
    const hp = r.u8();
    const state = r.u8();
    const hitCount = r.u8();
    if (hitCount > 24) throw new Error("bad npc hitCount");
    const hits: SnapshotBodyHit[] = [];
    for (let j = 0; j < hitCount; j++) {
      hits.push({ lx: r.f32(), ly: r.f32(), lz: r.f32(), r: r.f32() });
    }
    npcs.push({
      id, x, y, z, yaw, hp, state, hitCount, hits,
      bodyVox: r.copyBytes(BODY_VOX_GRID_BYTES),
      bodyVoxShell: r.copyBytes(BODY_VOX_GRID_BYTES),
      weapon: r.u8(),      // weapons
      weaponFlags: r.u8(), // weapons
    });
  }
  // v8: pickups в конце snapshot'а.
  const np = r.u8();
  const pickups: SnapshotPickup[] = [];
  for (let i = 0; i < np; i++) {
    pickups.push({ id: r.u16(), state: r.u8() });
  }
  // weapons: инвентарь получателя + снаряды.
  const yourOwned = r.u16();
  const yourAmmo: number[] = [];
  for (let i = 0; i < WEAPON_COUNT; i++) yourAmmo.push(r.i16());
  const npr = r.u8();
  const projectiles: SnapshotProjectile[] = [];
  for (let i = 0; i < npr; i++) {
    projectiles.push({
      id: r.u16(), kind: r.u8(),
      x: r.f32(), y: r.f32(), z: r.f32(),
      vx: r.f32(), vy: r.f32(), vz: r.f32(),
    });
  }
  const story = readStory(r); // story
  return {
    tick, lastAckedInput, yourId,
    matchPhase, roundIdx, phaseSecsLeft, teamAWins, teamBWins, teamAKills, teamBKills,
    players, npcs, pickups,
    yourOwned, yourAmmo, projectiles,
    story,
  };
}

/** Один pickup из MsgPickupInit (v8). Статические атрибуты — координаты,
 *  тип, amount. Клиент кеширует один раз; state обновляется через Snapshot. */
export interface PickupInit {
  id: number;       // u16
  type: PickupTypeValue;
  x: number;
  y: number;
  z: number;
  yaw: number;
  amount: number;   // int16
  state: number;    // u8
  /** artifacts: PickupArtifact.* (0 — обычный подбор). */
  artifact: number;
  /** artifacts: откуда вылетел (центр разрушения); у обычных = x,y,z. */
  fromX: number;
  fromY: number;
  fromZ: number;
}

/** Одна запись PickupInit (MsgPickupInit и EvPickupSpawn). */
export function readPickupRecord(r: Reader): PickupInit {
  return {
    id: r.u16(),
    type: r.u8() as PickupTypeValue,
    x: r.f32(), y: r.f32(), z: r.f32(),
    yaw: r.f32(),
    amount: r.i16(),
    state: r.u8(),
    // artifacts
    artifact: r.u8(),
    fromX: r.f32(), fromY: r.f32(), fromZ: r.f32(),
  };
}

export function readPickupInit(r: Reader): PickupInit[] {
  const n = r.u16();
  const out: PickupInit[] = [];
  for (let i = 0; i < n; i++) out.push(readPickupRecord(r));
  return out;
}

export interface HelloMsg {
  yourId: number;
  protocolVersion: number;
  tickRate: number; // Hz
  /** v7: ID сцены (arena1). Клиент ориентируется, что грузить из /api/chunks. */
  sceneId: string;
  /** v7: RoomKind — см. protocol.RoomKind. */
  roomKind: number;
  /** v7: MaxPlayers — показ в UI «X / max». */
  maxPlayers: number;
  /** v10: exponential height fog; density 0 = выкл. */
  fogColor: [number, number, number];
  fogDensity: number;
  fogHeight: number;
  fogHeightFalloff: number;
  /** v15: equirect sky URL (image or video); empty = procedural sky. */
  skyboxUrl: string;
  /** v17: статические лампы сцены (пусто — клиент берёт дефолт арены). */
  lights: HelloLight[];
  /** v20: точки взаимодействия (подсказка «Открыть», chunk id для ghost-коллизии). */
  interactHotspots: HelloInteractHotspot[];
  /** v22: режим матча (MatchMode.FFA/TDM). Для SP — FFA, roundsTotal=0. */
  matchMode: number;
  roundsTotal: number;
  roundSeconds: number;
  /** characters: персонаж и эффективные уровни ЭТОГО игрока; speedMul — для prediction. */
  charId: number;
  lvlHp: number;
  lvlSpeed: number;
  lvlArmor: number;
  speedMul: number;
  /** story: 1 — сюжетная комната «Книга заклинаний». */
  storyMode: number;
}

export interface HelloLight {
  x: number;
  y: number;
  z: number;
  range: number;
  r: number;
  g: number;
  b: number;
}

export function readHello(r: Reader): HelloMsg {
  const protocolVersion = r.u8();
  const yourId = r.u8();
  const tickRate = r.u16();
  const sceneId = r.str();
  const roomKind = r.u8();
  const maxPlayers = r.u8();
  const fogColor: [number, number, number] = [r.f32(), r.f32(), r.f32()];
  const fogDensity = r.f32();
  const fogHeight = r.f32();
  const fogHeightFalloff = r.f32();
  const skyboxUrl = r.str();
  const lights: HelloLight[] = [];
  if (protocolVersion >= 17 && r.remaining() > 0) {
    const n = r.u8();
    for (let i = 0; i < n; i++) {
      lights.push({
        x: r.f32(),
        y: r.f32(),
        z: r.f32(),
        range: r.f32(),
        r: r.u8(),
        g: r.u8(),
        b: r.u8(),
      });
    }
  }
  const interactHotspots: HelloInteractHotspot[] = [];
  if (protocolVersion >= 20 && r.remaining() > 0) {
    const n = r.u8();
    for (let i = 0; i < n; i++) {
      interactHotspots.push({
        kind: r.u8(),
        x: r.f32(),
        y: r.f32(),
        z: r.f32(),
        radius: r.f32(),
        chunkA: r.u16(),
        chunkB: r.u16(),
      });
    }
  }
  // v22: параметры матча.
  let matchMode = MatchMode.FFA as number;
  let roundsTotal = 0;
  let roundSeconds = 0;
  if (protocolVersion >= 22 && r.remaining() > 0) {
    matchMode = r.u8();
    roundsTotal = r.u8();
    roundSeconds = r.u16();
  }
  // characters (хвост Hello; старый сервер — нули → солдат, ×1).
  let charId = 0, lvlHp = 0, lvlSpeed = 0, lvlArmor = 0, speedMul = 1;
  if (r.remaining() >= 8) {
    charId = r.u8();
    lvlHp = r.u8();
    lvlSpeed = r.u8();
    lvlArmor = r.u8();
    speedMul = r.f32();
  }
  // story: байт режима после хвоста персонажа.
  const storyMode = r.remaining() >= 1 ? r.u8() : 0;
  return {
    protocolVersion,
    yourId,
    tickRate,
    sceneId,
    roomKind,
    maxPlayers,
    fogColor,
    fogDensity,
    fogHeight,
    fogHeightFalloff,
    skyboxUrl,
    lights,
    interactHotspots,
    matchMode,
    roundsTotal,
    roundSeconds,
    charId,
    lvlHp,
    lvlSpeed,
    lvlArmor,
    speedMul,
    storyMode,
  };
}

export interface SpawnCube {
  cubeIdx: number;
  rx: number; ry: number; rz: number;
}

export type GameEvent =
  | { type: typeof EvType.BlockBroken; playerId: number; blockIdx: number; vx: number; vy: number; vz: number }
  | { type: typeof EvType.Shot; shooterId: number; ox: number; oy: number; oz: number; hx: number; hy: number; hz: number; surface: number; /** weapons */ weapon: number }
  | { type: typeof EvType.Death; victimId: number; killerId: number }
  | { type: typeof EvType.Respawn; playerId: number; x: number; y: number; z: number; yaw: number }
  | { type: typeof EvType.ChunkBroken; chunkId: number; cubeIdx: number; vx: number; vy: number; vz: number }
  | {
      type: typeof EvType.FallingGroupSpawn;
      groupId: number;
      chunkId: number;
      cubeSize: number;
      pivot: [number, number, number];
      vel: [number, number, number];
      axis: [number, number, number];
      angVel: number;
      strength: number;
      cubes: SpawnCube[];
    }
  | {
      type: typeof EvType.FallingGroupUpdate;
      groupId: number;
      pos: [number, number, number];
      vel: [number, number, number];
      angle: number;
      angVel: number;
    }
  | { type: typeof EvType.FallingGroupShatter; groupId: number }
  | {
      type: typeof EvType.ChunkPoseUpdate;
      chunkId: number;
      pos: [number, number, number];
      vel: [number, number, number];
      quat: [number, number, number, number]; // W, X, Y, Z
      angVelVec: [number, number, number]; // world-space rad/s
      resting: boolean;
    }
  | {
      type: typeof EvType.NpcShot;
      npcId: number;
      ox: number; oy: number; oz: number;
      hx: number; hy: number; hz: number;
      /** 0 = попал в стену (промах); иначе ID жертвы-игрока. */
      victimId: number;
      /** weapons: id оружия бота */
      weapon: number;
    }
  | {
      type: typeof EvType.RoomClosed;
      reason: number;
    }
  | {
      type: typeof EvType.MatchEnd;
      reason: number;
      /** v22: Team.A/B (Team.None — ничья/FFA) */
      winnerTeam: number;
      /** v22: лучший игрок (в FFA — победитель матча) */
      winnerPlayerId: number;
      scores: { playerId: number; team: number; roundWins: number; kills: number; deaths: number; hits: number }[];
    }
  | { type: typeof EvType.RoundStart; roundIdx: number; roundsTotal: number }
  | {
      type: typeof EvType.RoundEnd;
      roundIdx: number;
      winnerTeam: number;
      winnerPlayerId: number;
      teamAKills: number;
      teamBKills: number;
    }
  | {
      type: typeof EvType.Pickup;
      pickupId: number;
      playerId: number;
      pickupType: PickupTypeValue;
      amount: number;
    }
  | {
      type: typeof EvType.PortalEnter;
      playerId: number;
      targetScene: string;
      targetPortal: string;
    }
  | {
      type: typeof EvType.NpcBlockBroken;
      npcId: number;
      blockIdx: number;
      vx: number; vy: number; vz: number;
    }
  | { type: typeof EvType.BodyDamaged; playerId: number; hx: number; hy: number; hz: number; vx: number; vy: number; vz: number }
  | { type: typeof EvType.NpcBodyDamaged; npcId: number; hx: number; hy: number; hz: number; vx: number; vy: number; vz: number; shatter: number; flags: number }
  | { type: typeof EvType.MansionDoor; phase: number }
  | { type: typeof EvType.InteriorDoor; doorIdx: number; phase: number }
  // weapons
  | {
      type: typeof EvType.WeaponFire;
      shooterKind: number; shooterId: number; weapon: number;
      ox: number; oy: number; oz: number;
      dx: number; dy: number; dz: number;
    }
  | { type: typeof EvType.Explosion; x: number; y: number; z: number; radius: number; weapon: number }
  | { type: typeof EvType.PickupSpawn; pickup: PickupInit }
  | { type: typeof EvType.PickupRemove; pickupId: number }
  /** story: событие сюжета (code — StoryEv; a/b/text — по коду). */
  | { type: typeof EvType.Story; code: number; mobId: number; a: number; b: number; x: number; y: number; z: number; text: string };

export function readEventBatch(r: Reader): GameEvent[] {
  const n = r.u16();
  const out: GameEvent[] = [];
  for (let i = 0; i < n; i++) {
    const t = r.u8();
    switch (t) {
      case EvType.BlockBroken:
        out.push({ type: EvType.BlockBroken, playerId: r.u8(), blockIdx: r.u8(), vx: r.f32(), vy: r.f32(), vz: r.f32() });
        break;
      case EvType.Shot:
        out.push({
          type: EvType.Shot, shooterId: r.u8(),
          ox: r.f32(), oy: r.f32(), oz: r.f32(),
          hx: r.f32(), hy: r.f32(), hz: r.f32(),
          surface: r.u8(),
          weapon: r.u8(), // weapons
        });
        break;
      case EvType.Death:
        out.push({ type: EvType.Death, victimId: r.u8(), killerId: r.u8() });
        break;
      case EvType.Respawn:
        out.push({ type: EvType.Respawn, playerId: r.u8(), x: r.f32(), y: r.f32(), z: r.f32(), yaw: r.f32() });
        break;
      case EvType.ChunkBroken:
        out.push({ type: EvType.ChunkBroken, chunkId: r.u16(), cubeIdx: r.u32(), vx: r.f32(), vy: r.f32(), vz: r.f32() });
        break;
      case EvType.FallingGroupSpawn: {
        const groupId = r.u16();
        const chunkId = r.u16();
        const cubeSize = r.f32();
        const pivot: [number, number, number] = [r.f32(), r.f32(), r.f32()];
        const vel: [number, number, number] = [r.f32(), r.f32(), r.f32()];
        const axis: [number, number, number] = [r.f32(), r.f32(), r.f32()];
        const angVel = r.f32();
        const strength = r.u8();
        const cnt = r.u16();
        const cubes: SpawnCube[] = [];
        for (let j = 0; j < cnt; j++) {
          cubes.push({ cubeIdx: r.u32(), rx: r.f32(), ry: r.f32(), rz: r.f32() });
        }
        out.push({ type: EvType.FallingGroupSpawn, groupId, chunkId, cubeSize, pivot, vel, axis, angVel, strength, cubes });
        break;
      }
      case EvType.FallingGroupUpdate: {
        const groupId = r.u16();
        const pos: [number, number, number] = [r.f32(), r.f32(), r.f32()];
        const vel: [number, number, number] = [r.f32(), r.f32(), r.f32()];
        const angle = r.f32();
        const angVel = r.f32();
        out.push({ type: EvType.FallingGroupUpdate, groupId, pos, vel, angle, angVel });
        break;
      }
      case EvType.FallingGroupShatter:
        out.push({ type: EvType.FallingGroupShatter, groupId: r.u16() });
        break;
      case EvType.ChunkPoseUpdate: {
        const chunkId = r.u16();
        const pos: [number, number, number] = [r.f32(), r.f32(), r.f32()];
        const vel: [number, number, number] = [r.f32(), r.f32(), r.f32()];
        const quat: [number, number, number, number] = [r.f32(), r.f32(), r.f32(), r.f32()];
        const angVelVec: [number, number, number] = [r.f32(), r.f32(), r.f32()];
        const resting = r.u8() !== 0;
        out.push({ type: EvType.ChunkPoseUpdate, chunkId, pos, vel, quat, angVelVec, resting });
        break;
      }
      case EvType.NpcShot: {
        out.push({
          type: EvType.NpcShot,
          npcId: r.u16(),
          ox: r.f32(), oy: r.f32(), oz: r.f32(),
          hx: r.f32(), hy: r.f32(), hz: r.f32(),
          victimId: r.u8(),
          weapon: r.u8(), // weapons
        });
        break;
      }
      case EvType.RoomClosed: {
        out.push({ type: EvType.RoomClosed, reason: r.u8() });
        break;
      }
      case EvType.MatchEnd: {
        const reason = r.u8();
        const winnerTeam = r.u8();
        const winnerPlayerId = r.u8();
        const cnt = r.u16();
        const scores: { playerId: number; team: number; roundWins: number; kills: number; deaths: number; hits: number }[] = [];
        for (let j = 0; j < cnt; j++) {
          scores.push({
            playerId: r.u8(), team: r.u8(), roundWins: r.u8(),
            kills: r.u16(), deaths: r.u16(), hits: r.u16(),
          });
        }
        out.push({ type: EvType.MatchEnd, reason, winnerTeam, winnerPlayerId, scores });
        break;
      }
      case EvType.Pickup: {
        out.push({
          type: EvType.Pickup,
          pickupId: r.u16(),
          playerId: r.u8(),
          pickupType: r.u8() as PickupTypeValue,
          amount: r.i16(),
        });
        break;
      }
      case EvType.PortalEnter: {
        out.push({
          type: EvType.PortalEnter,
          playerId: r.u8(),
          targetScene: r.str(),
          targetPortal: r.str(),
        });
        break;
      }
      case EvType.NpcBlockBroken: {
        out.push({
          type: EvType.NpcBlockBroken,
          npcId: r.u16(),
          blockIdx: r.u8(),
          vx: r.f32(), vy: r.f32(), vz: r.f32(),
        });
        break;
      }
      case EvType.BodyDamaged: {
        out.push({
          type: EvType.BodyDamaged,
          playerId: r.u8(),
          hx: r.f32(), hy: r.f32(), hz: r.f32(),
          vx: r.f32(), vy: r.f32(), vz: r.f32(),
        });
        break;
      }
      case EvType.NpcBodyDamaged: {
        out.push({
          type: EvType.NpcBodyDamaged,
          npcId: r.u16(),
          hx: r.f32(), hy: r.f32(), hz: r.f32(),
          vx: r.f32(), vy: r.f32(), vz: r.f32(),
          shatter: r.u8(),
          flags: r.u8(),
        });
        break;
      }
      case EvType.MansionDoor:
        out.push({ type: EvType.MansionDoor, phase: r.u8() });
        break;
      case EvType.InteriorDoor:
        out.push({ type: EvType.InteriorDoor, doorIdx: r.u8(), phase: r.u8() });
        break;
      case EvType.RoundStart:
        out.push({ type: EvType.RoundStart, roundIdx: r.u8(), roundsTotal: r.u8() });
        break;
      case EvType.RoundEnd:
        out.push({
          type: EvType.RoundEnd,
          roundIdx: r.u8(),
          winnerTeam: r.u8(),
          winnerPlayerId: r.u8(),
          teamAKills: r.u16(),
          teamBKills: r.u16(),
        });
        break;
      // weapons
      case EvType.WeaponFire:
        out.push({
          type: EvType.WeaponFire,
          shooterKind: r.u8(), shooterId: r.u16(), weapon: r.u8(),
          ox: r.f32(), oy: r.f32(), oz: r.f32(),
          dx: r.f32(), dy: r.f32(), dz: r.f32(),
        });
        break;
      case EvType.Explosion:
        out.push({ type: EvType.Explosion, x: r.f32(), y: r.f32(), z: r.f32(), radius: r.f32(), weapon: r.u8() });
        break;
      case EvType.PickupSpawn:
        out.push({ type: EvType.PickupSpawn, pickup: readPickupRecord(r) }); // artifacts: с хвостом artifact/from
        break;
      case EvType.PickupRemove:
        out.push({ type: EvType.PickupRemove, pickupId: r.u16() });
        break;
      case EvType.Story: // story
        out.push({
          type: EvType.Story, code: r.u8(), mobId: r.u16(), a: r.u16(), b: r.u16(),
          x: r.f32(), y: r.f32(), z: r.f32(), text: r.str(),
        });
        break;
      default:
        return out;
    }
  }
  return out;
}

export interface ChunkInit {
  chunkId: number;
  ox: number; oy: number; oz: number;
  sx: number; sy: number; sz: number;
  cubeSize: number;
  r: number; g: number; b: number;
  strength: number;
  /** Семантическая роль чанка (v8). См. ChunkRole enum. */
  role: ChunkRoleValue;
  texture: string;
  alive: Uint8Array;
  /** legacy per-cube UVs (для debris color sampling). null если объект не из mesh */
  uvs: Uint8Array | null;
  /** per-face corner UVs: alive_count × 6 граней × 4 угла × 4 байта (u16 u, u16 v).
   *  Углы (-T,-B),(+T,-B),(+T,+B),(-T,+B). V уже в screen convention (1-v).
   *  Используется как лукап-таблица: vert делает texelFetch, frag сэмплит ОРИГИНАЛЬНУЮ
   *  diffuse-текстуру (texture поле) по интерполированному UV. */
  faceUVs: Uint8Array | null;
  /** true = подчиняется физике (стулья, ящики). Клиент должен рендерить с трансформацией. */
  dynamic: boolean;
  /** true = чанк остался видимым через воксели даже когда есть static_meshes.glb
   *  (его меш вырезан из визуального GLB на этапе build_level). Используется
   *  для нод с явным префиксом `vox_`: их воксели — единственный визуал, и
   *  потому корректно дырявятся при разрушении. */
  voxVisual: boolean;
  /** Имя normal-map текстуры (`levelpacks/.../mat_N_n.png`), "" если нет.
   *  Применяется в voxel.frag для triplanar normal mapping. */
  normalTexture: string;
  /** 0=opaque, 1=mask (cutout), 2=blend. Для voxel-чанков blend сейчас
   *  тоже даёт cutout (без depth-sort), полный alpha-blend будет в v2. */
  alphaMode: number;
  /** Порог discard'а для AlphaMode=mask (default 0.5). */
  alphaCutoff: number;
  /** Сила нормал-мапы (gltf normalTexture.scale; 1.0 = default). */
  normalScale: number;
  /** Bbox в локальных coords чанка (для triplanar нормализации worldPos в шейдере) */
  bboxMin: [number, number, number];
  bboxSize: [number, number, number];
}

// Промежуточное представление: декомпрессия двух gzip-блоков (alive + uvs)
// делается асинхронно через DecompressionStream.
export interface ChunkInitRaw {
  chunkId: number;
  ox: number; oy: number; oz: number;
  sx: number; sy: number; sz: number;
  cubeSize: number;
  r: number; g: number; b: number;
  strength: number;
  role: ChunkRoleValue;
  texture: string;
  uncompressedAliveLen: number;
  aliveCompressed: Uint8Array;
  uncompressedUVsLen: number;
  uvsCompressed: Uint8Array | null;
  uncompressedFaceUVsLen: number;
  faceUVsCompressed: Uint8Array | null;
  dynamic: boolean;
  voxVisual: boolean;
  normalTexture: string;
  alphaMode: number;
  alphaCutoff: number;
  normalScale: number;
  bboxMin: [number, number, number];
  bboxSize: [number, number, number];
}

export function readChunkInit(r: Reader): ChunkInitRaw {
  const chunkId = r.u16();
  const ox = r.f32(), oy = r.f32(), oz = r.f32();
  const sx = r.u16(), sy = r.u16(), sz = r.u16();
  const cubeSize = r.f32();
  const cr = r.u8(), cg = r.u8(), cb = r.u8();
  const strength = r.u8();
  const role = r.u8() as ChunkRoleValue;
  const texture = r.str();
  // Bitmask: bit 0 = dynamic, bit 1 = vox_visual.
  // См. server/internal/proto/proto.go ChunkInit.Dynamic.
  const flags = r.u8();
  const dynamic = (flags & 0x01) !== 0;
  const voxVisual = (flags & 0x02) !== 0;
  // v18: PBR/alpha extras (normal map + alpha mode для levelpack-материалов).
  const normalTexture = r.str();
  const alphaMode = r.u8();
  const alphaCutoff = r.f32();
  const normalScale = r.f32();

  const uncompressedAliveLen = r.u32();
  const aliveCompressedLen = r.u32();
  const aliveCompressed = new Uint8Array(aliveCompressedLen);
  for (let i = 0; i < aliveCompressedLen; i++) aliveCompressed[i] = r.u8();

  const uncompressedUVsLen = r.u32();
  const uvsCompressedLen = r.u32();
  let uvsCompressed: Uint8Array | null = null;
  if (uvsCompressedLen > 0) {
    uvsCompressed = new Uint8Array(uvsCompressedLen);
    for (let i = 0; i < uvsCompressedLen; i++) uvsCompressed[i] = r.u8();
  }

  const uncompressedFaceUVsLen = r.u32();
  const faceUVsCompressedLen = r.u32();
  let faceUVsCompressed: Uint8Array | null = null;
  if (faceUVsCompressedLen > 0) {
    faceUVsCompressed = new Uint8Array(faceUVsCompressedLen);
    for (let i = 0; i < faceUVsCompressedLen; i++) faceUVsCompressed[i] = r.u8();
  }

  const bboxMin: [number, number, number] = [r.f32(), r.f32(), r.f32()];
  const bboxSize: [number, number, number] = [r.f32(), r.f32(), r.f32()];

  return {
    chunkId, ox, oy, oz, sx, sy, sz, cubeSize,
    r: cr, g: cg, b: cb, strength, role, texture,
    uncompressedAliveLen, aliveCompressed,
    uncompressedUVsLen, uvsCompressed,
    uncompressedFaceUVsLen, faceUVsCompressed,
    dynamic, voxVisual,
    normalTexture, alphaMode, alphaCutoff, normalScale,
    bboxMin, bboxSize,
  };
}

async function gunzip(buf: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream("gzip");
  const stream = new Blob([buf as unknown as BlobPart]).stream().pipeThrough(ds);
  const ab = await new Response(stream).arrayBuffer();
  return new Uint8Array(ab);
}

/** Распаковать оба сжатых блока (alive + опционально uvs). */
export async function decompressChunkInit(raw: ChunkInitRaw): Promise<ChunkInit> {
  const alive = await gunzip(raw.aliveCompressed);
  if (alive.byteLength !== raw.uncompressedAliveLen) {
    throw new Error(`chunk ${raw.chunkId}: alive size ${alive.byteLength} ≠ ${raw.uncompressedAliveLen}`);
  }
  let uvs: Uint8Array | null = null;
  if (raw.uvsCompressed !== null) {
    uvs = await gunzip(raw.uvsCompressed);
    if (uvs.byteLength !== raw.uncompressedUVsLen) {
      throw new Error(`chunk ${raw.chunkId}: uvs size ${uvs.byteLength} ≠ ${raw.uncompressedUVsLen}`);
    }
  }
  let faceUVs: Uint8Array | null = null;
  if (raw.faceUVsCompressed !== null) {
    faceUVs = await gunzip(raw.faceUVsCompressed);
    if (faceUVs.byteLength !== raw.uncompressedFaceUVsLen) {
      throw new Error(`chunk ${raw.chunkId}: face UVs size mismatch`);
    }
  }
  return {
    chunkId: raw.chunkId,
    ox: raw.ox, oy: raw.oy, oz: raw.oz,
    sx: raw.sx, sy: raw.sy, sz: raw.sz,
    cubeSize: raw.cubeSize,
    r: raw.r, g: raw.g, b: raw.b,
    strength: raw.strength,
    role: raw.role,
    texture: raw.texture,
    alive,
    uvs,
    faceUVs,
    dynamic: raw.dynamic,
    voxVisual: raw.voxVisual,
    normalTexture: raw.normalTexture,
    alphaMode: raw.alphaMode,
    alphaCutoff: raw.alphaCutoff,
    normalScale: raw.normalScale,
    bboxMin: raw.bboxMin,
    bboxSize: raw.bboxSize,
  };
}
